'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAdminBrokenLinkStats,
  getAdminCatalogSync,
  getAdminCatalogSyncFailures,
  getAdminContentHealthSnapshot,
  getAdminMatchIngest,
  getAdminMatchIngestFailures,
  getAdminMatchIngestLeagues,
  getAdminMatchIngestMatches,
  getAdminSerpHighlights,
  getAdminSerpHighlightsFailures,
  getAdminSyncRunStats,
  getAdminYoutubeChannelGaps,
  listAdminYoutubeHighlights,
  listAdminYoutubeScrapeAttempts,
  triggerCatalogSportSync,
  triggerCatalogSync,
  triggerMatchIngest,
  triggerMatchIngestSport,
  triggerSerpHighlights,
  triggerSerpHighlightsSport,
  waitForCatalogSyncIdle,
  waitForMatchIngestIdle,
  waitForSerpHighlightsIdle,
  type AdminCatalogSyncFailuresListParams,
  type AdminMatchIngestFailuresListParams,
  type AdminMatchIngestMatchesListParams,
  type AdminSerpHighlightsFailuresListParams,
  type AdminYoutubeHighlightsListParams,
  type AdminYoutubeScrapeAttemptsListParams,
} from '@nomiss/services';

const CONTENT_HEALTH_REFETCH_MS = 60_000;

export const adminContentHealthQueryKeys = {
  all: ['admin', 'content-health'] as const,
  snapshot: () => [...adminContentHealthQueryKeys.all, 'snapshot'] as const,
  catalogSync: () => [...adminContentHealthQueryKeys.all, 'catalog-sync'] as const,
  catalogSyncFailures: (filters: AdminCatalogSyncFailuresListParams) =>
    [...adminContentHealthQueryKeys.all, 'catalog-sync-failures', filters] as const,
  matchIngest: () => [...adminContentHealthQueryKeys.all, 'match-ingest'] as const,
  matchIngestLeagues: (sportCode: string) =>
    [...adminContentHealthQueryKeys.all, 'match-ingest-leagues', sportCode] as const,
  matchIngestMatches: (filters: AdminMatchIngestMatchesListParams) =>
    [...adminContentHealthQueryKeys.all, 'match-ingest-matches', filters] as const,
  matchIngestFailures: (filters: AdminMatchIngestFailuresListParams) =>
    [...adminContentHealthQueryKeys.all, 'match-ingest-failures', filters] as const,
  serpHighlights: () => [...adminContentHealthQueryKeys.all, 'serp-highlights'] as const,
  serpHighlightsFailures: (filters: AdminSerpHighlightsFailuresListParams) =>
    [...adminContentHealthQueryKeys.all, 'serp-highlights-failures', filters] as const,
  syncStats: () => [...adminContentHealthQueryKeys.all, 'sync-stats'] as const,
  brokenLinkStats: () =>
    [...adminContentHealthQueryKeys.all, 'broken-link-stats'] as const,
  youtubeHighlights: (filters: AdminYoutubeHighlightsListParams) =>
    [...adminContentHealthQueryKeys.all, 'youtube-highlights', filters] as const,
  youtubeScrapeAttempts: (filters: AdminYoutubeScrapeAttemptsListParams) =>
    [
      ...adminContentHealthQueryKeys.all,
      'youtube-scrape-attempts',
      filters,
    ] as const,
  youtubeChannelGaps: () =>
    [...adminContentHealthQueryKeys.all, 'youtube-channel-gaps'] as const,
};

export function useAdminContentHealthSnapshot(options?: { enabled?: boolean }) {
  const adminContentHealthSnapshotQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.snapshot(),
    queryFn: () => getAdminContentHealthSnapshot(),
    enabled: options?.enabled ?? true,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminContentHealthSnapshotQuery };
}

export function useAdminContentHealthProbe() {
  const queryClient = useQueryClient();

  const adminContentHealthProbeMutation = useMutation({
    mutationFn: () => getAdminContentHealthSnapshot({ probe: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(adminContentHealthQueryKeys.snapshot(), data);
    },
  });

  return { adminContentHealthProbeMutation };
}

export function useAdminCatalogSync(options?: {
  enabled?: boolean;
  refetchWhenIdleMs?: number;
}) {
  const adminCatalogSyncQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.catalogSync(),
    queryFn: () => getAdminCatalogSync(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.queueBusy === true
        ? 2_000
        : (options?.refetchWhenIdleMs ?? CONTENT_HEALTH_REFETCH_MS),
  });

  return { adminCatalogSyncQuery };
}

export function useAdminCatalogSyncFailures(
  filters: AdminCatalogSyncFailuresListParams,
  options?: { enabled?: boolean },
) {
  const adminCatalogSyncFailuresQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.catalogSyncFailures(filters),
    queryFn: () => getAdminCatalogSyncFailures(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminCatalogSyncFailuresQuery };
}

export function useAdminMatchIngest(options?: {
  enabled?: boolean;
  refetchWhenIdleMs?: number;
}) {
  const adminMatchIngestQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.matchIngest(),
    queryFn: () => getAdminMatchIngest(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.queueBusy === true
        ? 2_000
        : (options?.refetchWhenIdleMs ?? CONTENT_HEALTH_REFETCH_MS),
  });

  return { adminMatchIngestQuery };
}

export function useAdminSerpHighlights(options?: { enabled?: boolean }) {
  const adminSerpHighlightsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.serpHighlights(),
    queryFn: () => getAdminSerpHighlights(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) =>
      query.state.data?.queueBusy === true ? 2_000 : CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminSerpHighlightsQuery };
}

export function useAdminMatchIngestFailures(
  filters: AdminMatchIngestFailuresListParams,
  options?: { enabled?: boolean },
) {
  const adminMatchIngestFailuresQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.matchIngestFailures(filters),
    queryFn: () => getAdminMatchIngestFailures(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminMatchIngestFailuresQuery };
}

export function useAdminSerpHighlightsFailures(
  filters: AdminSerpHighlightsFailuresListParams,
  options?: { enabled?: boolean },
) {
  const adminSerpHighlightsFailuresQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.serpHighlightsFailures(filters),
    queryFn: () => getAdminSerpHighlightsFailures(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminSerpHighlightsFailuresQuery };
}

export function useAdminMatchIngestLeagues(
  sportCode: string,
  options?: { enabled?: boolean },
) {
  const adminMatchIngestLeaguesQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.matchIngestLeagues(sportCode),
    queryFn: () => getAdminMatchIngestLeagues(sportCode),
    enabled: (options?.enabled ?? true) && sportCode.length > 0,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminMatchIngestLeaguesQuery };
}

export function useAdminMatchIngestMatches(
  filters: AdminMatchIngestMatchesListParams,
  options?: { enabled?: boolean },
) {
  const adminMatchIngestMatchesQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.matchIngestMatches(filters),
    queryFn: () => getAdminMatchIngestMatches(filters),
    enabled: (options?.enabled ?? true) && filters.sportCode.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminMatchIngestMatchesQuery };
}

function invalidateCatalogSyncQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.catalogSync(),
  });
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.all,
  });
}

function invalidateMatchIngestQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.matchIngest(),
  });
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.all,
  });
}

function invalidateSerpHighlightsQueries(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.serpHighlights(),
  });
  void queryClient.invalidateQueries({
    queryKey: adminContentHealthQueryKeys.all,
  });
}

export const CATALOG_SYNC_ALL_SCOPE = 'all';
export const MATCH_INGEST_SYNC_ALL_SCOPE = 'all';
export const OTT_HIGHLIGHTS_SYNC_ALL_SCOPE = 'all';
const CATALOG_SYNC_SCOPE_KEY = 'admin.sync-teams-and-players.scope';
const MATCH_INGEST_SYNC_SCOPE_KEY = 'admin.sync-matches.scope';
const OTT_HIGHLIGHTS_SYNC_SCOPE_KEY = 'admin.sync-ott-highlights.scope';
const INGESTION_SYNC_RECENT_SCOPE_MS = 15_000;

type PersistedIngestionSyncScope = {
  scope: string;
  startedAt: number;
};

function readIngestionSyncScope(storageKey: string): PersistedIngestionSyncScope | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw === null) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      !('scope' in parsed) ||
      !('startedAt' in parsed) ||
      typeof parsed.scope !== 'string' ||
      typeof parsed.startedAt !== 'number'
    ) {
      return null;
    }
    return { scope: parsed.scope, startedAt: parsed.startedAt };
  } catch {
    return null;
  }
}

function writeIngestionSyncScope(storageKey: string, scope: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(
    storageKey,
    JSON.stringify({ scope, startedAt: Date.now() }),
  );
}

function clearIngestionSyncScope(storageKey: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(storageKey);
}

function useResumeIngestionSync(options: {
  ready: boolean;
  queueBusy: boolean;
  storageKey: string;
  allScope: string;
  waitUntilIdle: (options?: { onProgress?: () => void }) => Promise<void>;
  invalidate: (queryClient: ReturnType<typeof useQueryClient>) => void;
  onResumedComplete?: () => void;
  onResumedError?: (error: unknown) => void;
}) {
  const queryClient = useQueryClient();
  const [activeScope, setActiveScope] = useState<string | null>(null);
  const startedInSessionRef = useRef(false);
  const resumeStartedRef = useRef(false);
  const unmountedRef = useRef(false);
  const onResumedCompleteRef = useRef(options.onResumedComplete);
  const onResumedErrorRef = useRef(options.onResumedError);
  const waitUntilIdleRef = useRef(options.waitUntilIdle);
  const invalidateRef = useRef(options.invalidate);
  onResumedCompleteRef.current = options.onResumedComplete;
  onResumedErrorRef.current = options.onResumedError;
  waitUntilIdleRef.current = options.waitUntilIdle;
  invalidateRef.current = options.invalidate;

  useLayoutEffect(() => {
    const persisted = readIngestionSyncScope(options.storageKey);
    if (persisted !== null) {
      setActiveScope(persisted.scope);
    }
  }, [options.storageKey]);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    if (!options.ready || startedInSessionRef.current || resumeStartedRef.current) {
      return;
    }

    const persisted = readIngestionSyncScope(options.storageKey);
    const recent =
      persisted !== null && Date.now() - persisted.startedAt < INGESTION_SYNC_RECENT_SCOPE_MS;
    if (!options.queueBusy && !recent) {
      clearIngestionSyncScope(options.storageKey);
      setActiveScope(null);
      return;
    }

    resumeStartedRef.current = true;
    setActiveScope(persisted?.scope ?? options.allScope);

    void (async () => {
      try {
        await waitUntilIdleRef.current({
          onProgress: () => invalidateRef.current(queryClient),
        });
        if (unmountedRef.current) {
          return;
        }
        clearIngestionSyncScope(options.storageKey);
        setActiveScope(null);
        invalidateRef.current(queryClient);
        onResumedCompleteRef.current?.();
      } catch (error) {
        if (unmountedRef.current) {
          return;
        }
        onResumedErrorRef.current?.(error);
      }
    })();
  }, [options.ready, options.queueBusy, options.storageKey, options.allScope, queryClient]);

  function markSyncStarted(scope: string) {
    startedInSessionRef.current = true;
    writeIngestionSyncScope(options.storageKey, scope);
    setActiveScope(scope);
  }

  function markSyncFinished() {
    clearIngestionSyncScope(options.storageKey);
    setActiveScope(null);
  }

  return { activeScope, markSyncStarted, markSyncFinished };
}

export function useResumeCatalogSync(options: {
  ready: boolean;
  queueBusy: boolean;
  onResumedComplete?: () => void;
  onResumedError?: (error: unknown) => void;
}) {
  return useResumeIngestionSync({
    ready: options.ready,
    queueBusy: options.queueBusy,
    storageKey: CATALOG_SYNC_SCOPE_KEY,
    allScope: CATALOG_SYNC_ALL_SCOPE,
    waitUntilIdle: waitForCatalogSyncIdle,
    invalidate: invalidateCatalogSyncQueries,
    ...(options.onResumedComplete !== undefined
      ? { onResumedComplete: options.onResumedComplete }
      : {}),
    ...(options.onResumedError !== undefined
      ? { onResumedError: options.onResumedError }
      : {}),
  });
}

export function useResumeMatchIngestSync(options: {
  ready: boolean;
  queueBusy: boolean;
  onResumedComplete?: () => void;
  onResumedError?: (error: unknown) => void;
}) {
  return useResumeIngestionSync({
    ready: options.ready,
    queueBusy: options.queueBusy,
    storageKey: MATCH_INGEST_SYNC_SCOPE_KEY,
    allScope: MATCH_INGEST_SYNC_ALL_SCOPE,
    waitUntilIdle: waitForMatchIngestIdle,
    invalidate: invalidateMatchIngestQueries,
    ...(options.onResumedComplete !== undefined
      ? { onResumedComplete: options.onResumedComplete }
      : {}),
    ...(options.onResumedError !== undefined
      ? { onResumedError: options.onResumedError }
      : {}),
  });
}

export function useResumeSerpHighlightsSync(options: {
  ready: boolean;
  queueBusy: boolean;
  onResumedComplete?: () => void;
  onResumedError?: (error: unknown) => void;
}) {
  return useResumeIngestionSync({
    ready: options.ready,
    queueBusy: options.queueBusy,
    storageKey: OTT_HIGHLIGHTS_SYNC_SCOPE_KEY,
    allScope: OTT_HIGHLIGHTS_SYNC_ALL_SCOPE,
    waitUntilIdle: waitForSerpHighlightsIdle,
    invalidate: invalidateSerpHighlightsQueries,
    ...(options.onResumedComplete !== undefined
      ? { onResumedComplete: options.onResumedComplete }
      : {}),
    ...(options.onResumedError !== undefined
      ? { onResumedError: options.onResumedError }
      : {}),
  });
}

function useIdleWait(
  wait: (options?: { onProgress?: () => void }) => Promise<void>,
  invalidate: (queryClient: ReturnType<typeof useQueryClient>) => void,
) {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    await wait({
      onProgress: () => invalidate(queryClient),
    });
    invalidate(queryClient);
  }, [invalidate, queryClient, wait]);
}

export function useTriggerCatalogSync() {
  const triggerCatalogSyncMutation = useMutation({
    mutationFn: () => triggerCatalogSync(true),
  });
  const waitForCatalogIdle = useIdleWait(
    waitForCatalogSyncIdle,
    invalidateCatalogSyncQueries,
  );

  return { triggerCatalogSyncMutation, waitForCatalogIdle };
}

export function useTriggerCatalogSportSync() {
  const triggerCatalogSportSyncMutation = useMutation({
    mutationFn: (sportCode: string) => triggerCatalogSportSync(sportCode, true),
  });
  const waitForCatalogIdle = useIdleWait(
    waitForCatalogSyncIdle,
    invalidateCatalogSyncQueries,
  );

  return { triggerCatalogSportSyncMutation, waitForCatalogIdle };
}

export function useTriggerMatchIngest() {
  const triggerMatchIngestMutation = useMutation({
    mutationFn: () => triggerMatchIngest(true),
  });
  const waitForMatchIdle = useIdleWait(
    waitForMatchIngestIdle,
    invalidateMatchIngestQueries,
  );

  return { triggerMatchIngestMutation, waitForMatchIdle };
}

export function useTriggerMatchIngestSport() {
  const triggerMatchIngestSportMutation = useMutation({
    mutationFn: (sportCode: string) => triggerMatchIngestSport(sportCode, true),
  });
  const waitForMatchIdle = useIdleWait(
    waitForMatchIngestIdle,
    invalidateMatchIngestQueries,
  );

  return { triggerMatchIngestSportMutation, waitForMatchIdle };
}

export function useTriggerSerpHighlights() {
  const triggerSerpHighlightsMutation = useMutation({
    mutationFn: () => triggerSerpHighlights(),
  });
  const waitForSerpIdle = useIdleWait(
    waitForSerpHighlightsIdle,
    invalidateSerpHighlightsQueries,
  );

  return { triggerSerpHighlightsMutation, waitForSerpIdle };
}

export function useTriggerSerpHighlightsSport() {
  const triggerSerpHighlightsSportMutation = useMutation({
    mutationFn: (sportCode: string) => triggerSerpHighlightsSport(sportCode),
  });
  const waitForSerpIdle = useIdleWait(
    waitForSerpHighlightsIdle,
    invalidateSerpHighlightsQueries,
  );

  return { triggerSerpHighlightsSportMutation, waitForSerpIdle };
}

export function useAdminSyncRunStats(options?: { enabled?: boolean }) {
  const adminSyncRunStatsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.syncStats(),
    queryFn: () => getAdminSyncRunStats(),
    enabled: options?.enabled ?? true,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminSyncRunStatsQuery };
}

export function useAdminContentHealthBrokenLinkStats(options?: {
  enabled?: boolean;
}) {
  const adminContentHealthBrokenLinkStatsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.brokenLinkStats(),
    queryFn: () => getAdminBrokenLinkStats(),
    enabled: options?.enabled ?? true,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminContentHealthBrokenLinkStatsQuery };
}

export function useAdminYoutubeHighlights(
  filters: AdminYoutubeHighlightsListParams,
  options?: { enabled?: boolean },
) {
  const adminYoutubeHighlightsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.youtubeHighlights(filters),
    queryFn: () => listAdminYoutubeHighlights(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminYoutubeHighlightsQuery };
}

export function useAdminYoutubeScrapeAttempts(
  filters: AdminYoutubeScrapeAttemptsListParams,
  options?: { enabled?: boolean },
) {
  const adminYoutubeScrapeAttemptsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.youtubeScrapeAttempts(filters),
    queryFn: () => listAdminYoutubeScrapeAttempts(filters),
    enabled: options?.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminYoutubeScrapeAttemptsQuery };
}

export function useAdminYoutubeChannelGaps(options?: { enabled?: boolean }) {
  const adminYoutubeChannelGapsQuery = useQuery({
    queryKey: adminContentHealthQueryKeys.youtubeChannelGaps(),
    queryFn: () => getAdminYoutubeChannelGaps(),
    enabled: options?.enabled ?? true,
    refetchInterval: CONTENT_HEALTH_REFETCH_MS,
  });

  return { adminYoutubeChannelGapsQuery };
}
